import api, { API_BASE } from "./api";

// Multi-club coach support — a coach (primary or secondary) can belong to
// more than one club, switch which one is "active" for their session, or
// register a brand-new club on top of an existing account (no OTP gate here,
// unlike ClubRegistration.jsx's first-time flow — the coach is already
// authenticated). Backed by CoachClubController; this file is the first
// frontend consumer of these routes (see the auto-memory note on this — the
// backend already fully supported this before any UI existed for it).

// GET /api/coach/clubs — every club this coach belongs to, which one is
// currently "active" (active_club_id, mirrors member_registration.club_id —
// the same field CoachDashboard.jsx's `coach.club_id` already drives
// everything from), and any additional-club registrations still awaiting
// payment (`pending_additional_clubs` never appear in `clubs` — a row only
// gets a real club_registration/club_coaches record once paid).
export const listCoachClubs = () => api.get("/api/coach/clubs").then((r) => r.data);

// POST /api/coach/active-club { club_id } — switch the coach's active club.
// 403s server-side if the coach isn't actually attached to that club.
export const selectActiveClub = (clubId) =>
  api.post("/api/coach/active-club", { club_id: clubId }).then((r) => r.data);

// GET /api/coach/clubs/additional/primary-coach — the logged-in coach's own
// profile, locked read-only, for the "register another club" form's primary
// coach section (only club details + optional secondary coaches are editable
// there — the backend rejects any attempt to change the primary coach here).
export const getPrimaryCoachProfile = () =>
  api.get("/api/coach/clubs/additional/primary-coach").then((r) => r.data);

// POST /api/coach/clubs/additional/fee-preview { secondary_coach_count } —
// live fee estimate as secondary coaches are added/removed. The base fee
// already covers the primary coach + 1 secondary (INCLUDED_COACHES = 2 on
// the backend, same rule as the original club-registration-fee-preview).
export const getAdditionalClubFeePreview = (secondaryCoachCount) =>
  api
    .post("/api/coach/clubs/additional/fee-preview", { secondary_coach_count: secondaryCoachCount })
    .then((r) => r.data);

// POST /api/coach/clubs/additional/preview (multipart) — backend validation
// before the review step. `errors` on a 422 use the same coaches.{i}.{field}
// dot-path shape as club-registrations/preview; response `data` normalizes
// coach names and carries the authoritative fee breakdown, plus the locked
// primary coach as coaches[0] with coach_role: "Primary Coach".
export const previewAdditionalClub = (formData) =>
  api.post("/api/coach/clubs/additional/preview", formData).then((r) => r.data);

// POST /api/coach/clubs/additional (multipart) — creates the additional-club
// registration PROCESS (not a real club/membership yet) and returns
// { process_id, payable_amount, payment_reference, payment_payload, ... },
// the same shape ClubRegistration.jsx's store response uses, so it can be
// paid the same way (card via the real payment_payload, or pay-at-counter).
export const registerAdditionalClub = (formData) =>
  api.post("/api/coach/clubs/additional", formData).then((r) => r.data);

// Printable "pay at counter" voucher — a public, unauthenticated route
// (ApplicantShroffVoucherController::clubRegistration) proven by
// processId + payment_reference rather than a session, same one
// PaymentMethod.jsx links to for the original club-registration flow. The
// backend streams the PDF with Content-Disposition: attachment, so a plain
// link triggers the browser's native download.
export const clubApplicantShroffVoucherUrl = (processId, paymentReference) =>
  processId && paymentReference
    ? `${API_BASE}/api/registration-payments/clubs/${processId}/applicant-shroff-voucher?payment_reference=${encodeURIComponent(paymentReference)}`
    : null;
